import { Shadow, ToastAlertTypes, VStack } from '@genial/components';
import { BoxRadius, Spacing } from '@genial/design-tokens';
import { ErrorListResponse, instanceOfErrorListResponse } from '@genial/services';
import { MetaResponse } from '@genial/services/dist/models';
import { Utils } from '@genial/utils';
import { Box } from 'native-base';
import React, { useEffect, useMemo, useState } from 'react';
import { FlatList } from 'react-native';
import { RecoilState, RecoilValueReadOnly, useRecoilState } from 'recoil';

import { useRecoilQuery } from '../../../../../../hooks/useRecoilQuery';
import { useToastCreator } from '../../../../../../hooks/useToastCreator';
import { DynamicProductQueryParams } from '../../../../../../store/availableProducts/types';
import { emptyOffersLoadedState } from '../../../../../../store/product/atom';
import { DynamicCustomFiltersProps } from '../../../../../../store/product/types';
import { ListBottom } from '../../../../../BatchTicket/components/DynamicAsset/components/Asset/components/ListBottom';
import { AssetsLoading } from '../../../../../BatchTicket/components/DynamicAsset/components/AssetsLoading';
import { flatListProps } from '../../../../../BatchTicket/utils';
import { useGlobalProductContext } from '../../contexts';

import { ListEmpty } from './components/ListEmpty';
import { ProductTableHead } from './components/ProductTableHead';

type ProductsQueryResult<T> = RecoilValueReadOnly<
  | {
      meta: MetaResponse;
      data: T[];
    }
  | undefined
>;

const defaultErrorMessage: [ToastAlertTypes.Type, string, string] = [
  'error',
  'Erro ao carregar os produtos.',
  'Ocorreu um erro ao carregar os produtos,\nverifique sua conexão com a internet e\ntente novamente em instantes.',
];

export namespace ProductTypes {
  export interface Props<T> {
    productsQuery: (param: DynamicProductQueryParams) => ProductsQueryResult<T>;
    customFiltersQuery: (param: undefined) => RecoilValueReadOnly<DynamicCustomFiltersProps>;
    ProductItem: React.FC<{ offer: T }>;
    ProductItemLoading: React.FC;
    atomToSetProductsLoaded?: RecoilState<{ data: T[]; meta: MetaResponse; loaded: boolean }>;
  }
}

export const Product = <T,>({
  productsQuery,
  customFiltersQuery,
  ProductItem,
  ProductItemLoading,
  atomToSetProductsLoaded,
}: ProductTypes.Props<T>) => {
  const [productsAtomQuery, productsAtomQuerySetter] = useRecoilState(
    atomToSetProductsLoaded || emptyOffersLoadedState
  );
  const { showToast, showToastFromError } = useToastCreator('products-available-toast');
  const {
    product,
    orderBy,
    isDesktop,
    searchTerm,
    currentPage,
    filtersToApply,
    loadingProducts,
    loadingInitialProducts,
    loadingMoreProducts,
    setCurrentPage,
    setCurrentRefresher,
    setAvailableFilters,
    setLoadingCategories,
    setLoadingProducts,
    setProductCategories,
  } = useGlobalProductContext();

  const [allData, setAllData] = useState<T[]>([]);
  const [meta, setMeta] = useState<MetaResponse>();

  const { data, loading, refresher, error } = useRecoilQuery(
    productsQuery({
      filtersToApply,
      page: currentPage,
      searchTerm,
      orderBy,
    })
  );

  const {
    data: filtersData,
    loading: loadingFilters,
    refresher: filtersRefresher,
  } = useRecoilQuery(customFiltersQuery(undefined));

  const handleEndReached = () => {
    if (!meta?.pagination.hasNext || loading || error) {
      return;
    }
    setCurrentPage((prev) => prev + 1);
  };

  useEffect(() => {
    const emptyFilters = [] as any;
    setLoadingCategories(loadingFilters);
    setAvailableFilters(product.mountFilters(filtersData || emptyFilters));
    if (product.hasCategories) {
      setProductCategories(product.mountCategories(filtersData || emptyFilters));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersData, loadingFilters]);

  useEffect(() => {
    setCurrentRefresher(() => refresher);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresher]);

  useEffect(() => {
    setMeta(data?.meta);
    setLoadingProducts(loading);
    if (data && data.meta.pagination.page !== currentPage) {
      return;
    }
    if (data && currentPage === 1) {
      setAllData(data.data);
    }
    if (data && currentPage > 1) {
      setAllData((prev) => [...prev, ...data.data]);
    }
    if (data && atomToSetProductsLoaded && !productsAtomQuery.loaded) {
      productsAtomQuerySetter({ ...data, loaded: true });
      filtersRefresher();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, data]);

  useEffect(() => {
    if (!error) {
      return;
    }
    if (instanceOfErrorListResponse(error)) {
      (error as unknown as ErrorListResponse).errors?.forEach((errorItem) =>
        showToastFromError(errorItem)
      );
      return;
    }

    showToast(...defaultErrorMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const flatListMemoized = useMemo(() => {
    return (
      <Shadow level={2} borderTopRadius={BoxRadius.sm}>
        <VStack maxH="500px" overflowY="auto">
          <FlatList
            data={allData}
            {...flatListProps}
            maxToRenderPerBatch={20}
            updateCellsBatchingPeriod={5}
            keyExtractor={(_, index) => index.toString()}
            showsVerticalScrollIndicator={false}
            ListEmptyComponent={
              <ListEmpty
                hasError={!!error}
                hasData={!!allData.length}
                isLoading={loadingProducts}
                ProductItemLoading={ProductItemLoading}
              />
            }
            contentContainerStyle={{
              marginVertical: Utils.Token.ToInt(Spacing, 'stack.nano'),
            }}
            onEndReached={handleEndReached}
            onEndReachedThreshold={0.2}
            ListFooterComponent={
              <ListBottom
                hasData={!!allData.length}
                hasNextPage={meta?.pagination.hasNext || false}
                isLoading={loadingProducts}
                isLoadingMore={loadingMoreProducts}
                ProductItemLoading={ProductItemLoading}
              />
            }
            ItemSeparatorComponent={() => (
              <Box style={{ height: Utils.Token.ToInt(Spacing, 'stack.nano') }} />
            )}
            renderItem={({ item: offer }) => {
              return <ProductItem offer={offer} />;
            }}
          />
        </VStack>
      </Shadow>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allData, loadingProducts, loadingMoreProducts]);

  const renderPrincipal = () => {
    if (loadingInitialProducts) {
      return <AssetsLoading ProductItemLoading={ProductItemLoading} quantity={5} />;
    }

    return flatListMemoized;
  };

  return (
    <VStack>
      {!!isDesktop && <ProductTableHead recurses={product.tableHead} />}
      {renderPrincipal()}
    </VStack>
  );
};
